import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
    Bell,
    CheckCircle,
    UserPlus,
    X,
    Check,
    ArrowClockwise,
} from '@phosphor-icons/react';

const Notifications = () => {
    const { t, language } = useLanguage();

    const [notifications, setNotifications] = useState([]);
    const [incomingRequests, setIncomingRequests] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const loadData = async () => {
        try {
            setLoading(true);

            const [notificationsResponse, requestsResponse] = await Promise.all([
                api.getNotifications({ limit: 50 }),
                api.getIncomingFriendRequests(),
            ]);

            setNotifications(notificationsResponse.data?.items || []);
            setUnreadCount(notificationsResponse.data?.unread_count || 0);
            setIncomingRequests(requestsResponse.data || []);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleAccept = async (requestId) => {
        try {
            await api.acceptFriendRequest(requestId);
            await loadData();
        } catch (error) {
            console.error('Failed to accept friend request:', error);
        }
    };

    const handleReject = async (requestId) => {
        try {
            await api.rejectFriendRequest(requestId);
            await loadData();
        } catch (error) {
            console.error('Failed to reject friend request:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.markAllNotificationsRead();
            await loadData();
        } catch (error) {
            console.error('Failed to mark notifications as read:', error);
        }
    };

    const getUnreadText = () => {
        if (unreadCount === 1) {
            return `1 ${t.unreadNotification}`;
        }

        return `${unreadCount} ${t.unreadNotifications}`;
    };

    const getFriendRequestText = (sender) => {
        const name = sender?.display_name || sender?.username || t.unknownUser;

        if (language === 'ko') {
            return `${name}${t.wantsToBeFriend}`;
        }

        return `${name} ${t.wantsToBeFriend}`;
    };

    return (
        <div className="min-h-screen bg-[#050505] pb-28">
            <Navbar />

            <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
                <div className="flex items-start justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <Bell size={40} weight="duotone" className="text-cyan-400" />

                            <h1
                                className="text-4xl sm:text-5xl tracking-tighter font-black text-white"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                {t.notificationsTitle}
                            </h1>
                        </div>

                        <p className="text-gray-400">{t.notificationsSubtitle}</p>
                    </div>

                    <button
                        type="button"
                        onClick={loadData}
                        title={t.refresh}
                        className="w-11 h-11 rounded-full border border-white/10 bg-white/5 text-gray-300 hover:text-cyan-400 hover:border-cyan-400/50 flex items-center justify-center transition-all"
                    >
                        <ArrowClockwise size={22} />
                    </button>
                </div>

                {unreadCount > 0 && (
                    <div className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-5 py-4">
                        <p className="text-cyan-200 font-semibold">{getUnreadText()}</p>

                        <button
                            type="button"
                            onClick={handleMarkAllRead}
                            className="text-sm font-bold text-cyan-300 hover:text-cyan-100 transition-colors"
                        >
                            {t.markAllAsRead}
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="rounded-3xl border border-white/10 bg-black/60 p-10 text-center text-gray-400">
                        {t.loadingNotifications}
                    </div>
                ) : (
                    <div className="space-y-8">
                        <section className="rounded-3xl border border-white/10 bg-black/60 p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <UserPlus size={28} weight="duotone" className="text-cyan-400" />
                                <h2
                                    className="text-2xl font-black text-white"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                >
                                    {t.friendRequests}
                                </h2>
                            </div>

                            {incomingRequests.length === 0 ? (
                                <p className="text-gray-500">{t.noFriendRequests}</p>
                            ) : (
                                <div className="space-y-4">
                                    {incomingRequests.map((item) => {
                                        const sender = item.sender;

                                        return (
                                            <div
                                                key={item.request_id}
                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[#0A0E17] p-5"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-full bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center text-cyan-300 font-black">
                                                        {(sender?.display_name || sender?.username || 'U')
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>

                                                    <div>
                                                        <p className="text-white font-bold">
                                                            {sender?.display_name || sender?.username || t.unknownUser}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            @{sender?.username || 'unknown'} · {getFriendRequestText(sender)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAccept(item.request_id)}
                                                        className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-4 py-2 text-sm font-black text-black hover:bg-cyan-300 transition-colors"
                                                    >
                                                        <Check size={18} weight="bold" />
                                                        {t.accept}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleReject(item.request_id)}
                                                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-gray-300 hover:text-red-300 hover:border-red-400/40 transition-colors"
                                                    >
                                                        <X size={18} weight="bold" />
                                                        {t.reject}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        <section className="rounded-3xl border border-white/10 bg-black/60 p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Bell size={28} weight="duotone" className="text-cyan-400" />
                                <h2
                                    className="text-2xl font-black text-white"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                >
                                    {t.updates}
                                </h2>
                            </div>

                            {notifications.length === 0 ? (
                                <p className="text-gray-500">{t.noNotifications}</p>
                            ) : (
                                <div className="space-y-4">
                                    {notifications.map((item) => (
                                        <div
                                            key={item.notification_id}
                                            className={`rounded-2xl border p-5 ${item.read
                                                ? 'border-white/10 bg-[#0A0E17]'
                                                : 'border-cyan-400/30 bg-cyan-400/10'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="text-white font-bold">{item.title}</p>
                                                    <p className="text-sm text-gray-400 mt-1">{item.message}</p>
                                                </div>

                                                {!item.read && (
                                                    <CheckCircle
                                                        size={22}
                                                        weight="fill"
                                                        className="text-cyan-400 flex-shrink-0"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Notifications;